import React, { useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import SimpleBar from "simplebar-react";
import MetisMenu from "metismenujs";
import withRouter from "components/Common/withRouter";
import { withTranslation } from "react-i18next";

const SidebarContent = ({ t, authUser }) => {
  const ref = useRef();
  const path = useLocation();

  // Récupération du rôle (Redux ou localStorage)
  const user = authUser || JSON.parse(localStorage.getItem("authUser"));
  const role = user?.account?.role?.toLowerCase(); // converti en minuscule pour simplifier

  // Structure du menu avec rôle autorisé
  const menuItems = [
    {
      title: "Tableau de bord",
      path: "/dashboard",
      icon: "bx bx-home-circle",
      allowedRoles: ["administrator", "superuser", "teacher", "student"],
    },
    {
      title: "Gestion académique",
      icon: "bx bx-buildings",
      allowedRoles: ["administrator", "superuser"],
      subMenu: [
        { title: "Facultés", path: "/faculties", allowedRoles: ["administrator", "superuser"] },
        { title: "Départements", path: "/departments", allowedRoles: ["administrator", "superuser"] },
        { title: "Services", path: "/services", allowedRoles: ["teacher", "superuser"] },
        { title: "Types de formations", path: "/training-types", allowedRoles: ["administrator", "superuser"] },
        { title: "Filières", path: "/branches", allowedRoles: ["administrator", "superuser"] },
        { title: "Cycles", path: "/academic-cycle-configs", allowedRoles: ["administrator", "superuser"] },
        { title: "Structures de stage", path: "/internship-structures", allowedRoles: ["administrator", "superuser"] },
        { title: "Semestres", path: "/semesters", allowedRoles: ["administrator", "superuser"] },

        { title: "Niveaux d'études", path: "/study-levels", allowedRoles: ["administrator", "superuser"] },
        { title: "Unités d'enseignement", path: "/course-units", allowedRoles: ["administrator", "superuser"] },
        { title: "Éléments constitutifs", path: "/course-components", allowedRoles: ["administrator", "superuser"] },
        { title: "Types documents", path: "/document-types", allowedRoles: ["administrator", "superuser"] },
        { title: "Prestations", path: "/prestations", allowedRoles: ["administrator", "superuser"] },
        { title: "Types de frais", path: "/fee-types", allowedRoles: ["administrator", "superuser"] },
        //{ title: "Titres", path: "/titles", allowedRoles: ["administrator", "superuser"] },
        { title: "Affectations enseignant", path: "/teacher-assignments", allowedRoles: ["administrator", "superuser"] },
        { title: "Staffs", path: "/staffs", allowedRoles: ["administrator", "superuser"] },
        { title: "Années académiques", path: "/academic-years", allowedRoles: ["administrator", "superuser"] },
        { title: "Configurations préinscription", path: "/pre-registration-configurations", allowedRoles: ["administrator", "superuser"] },
      ],
    },
    {
      title: "Étudiants",
      icon: "fas fa-user-graduate",
      allowedRoles: ["administrator", "superuser"],
      subMenu: [
        { title: "Préinscriptions", path: "/pre-registrations", allowedRoles: [ "superuser"] },
        { title: "Étudiants", path: "/students", allowedRoles: [ "superuser"] },
        { title: "Inscriptions", path: "/registrations", allowedRoles: [ "superuser"] },
        { title: "Demandes de transferts", path: "/transfer-requests", allowedRoles: ["superuser"] },
        //{ title: "Évaluations", path: "/evaluations", allowedRoles: ["teacher", "superuser"] },
        //{ title: "Notes", path: "/notes", allowedRoles: ["teacher", "superuser"] },
        { title: "Stages", path: "/interships", allowedRoles: ["superuser"] },
        { title: "Demandes de documents", path: "/document-requests", allowedRoles: ["superuser"] },
        { title: "Soutenances", path: "/defenses", allowedRoles: ["superuser"] },
      ],
    },
    {
      title: "Enseignants",
      icon: "fas fa-chalkboard-teacher",
      allowedRoles: ["administrator", "superuser"],
      subMenu: [{ title: "Enseignants", path: "/teachers", allowedRoles: ["teacher", "superuser", "administrator"] }],
    },

    {
      title: "Gestion des examens",
      icon: "fas fa-user-graduate",
      allowedRoles: ["administrator", "superuser", "teacher"],
      subMenu: [


        { title: "Sessions", path: "/sessions", allowedRoles: ["administrator", "superuser"] },
        { title: "Évaluations", path: "/evaluations", allowedRoles: ["administrator", "superuser"] },
        { title: "Salles d'examen", path: "/exam-rooms", allowedRoles: ["administrator", "superuser"] },
        { title: "Notations", path: "/notations", allowedRoles: ["teacher"] },
        //{ title: "Notes", path: "/notes", allowedRoles: ["teacher", "superuser"] },
       
      ],
    },
    /*
    {
      title: "Paiements",
      icon: "bx bx-credit-card",
      allowedRoles: ["administrator", "superuser"],
      subMenu: [{ title: "Paiements", path: "/payments", allowedRoles: ["administrator", "superuser"] }],
    },
    */
    {
      title: "Paramètres",
      icon: "bx bx-slider",
      allowedRoles: ["administrator", "superuser"],
      subMenu: [
        { title: "Roles", path: "/roles", allowedRoles: ["administrator", "superuser"] },
        { title: "Permissions", path: "/features", allowedRoles: ["administrator", "superuser"] },
        //{ title: "Droits d'utilisateurs", path: "/role-permissions", allowedRoles: ["administrator", "superuser"] },
        { title: "Utilisateurs", path: "/accounts", allowedRoles: ["administrator", "superuser"] },
        { title: "Types de fichiers", path: "/file-types", allowedRoles: ["administrator", "superuser"] },
        { title: "Fonctionalités/Types fichiers", path: "/feature-file-types", allowedRoles: ["administrator", "superuser"] },
      ],
    },
  ];

  // Active menu et MetisMenu
  const activateParentDropdown = useCallback((item) => {
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];
    if (parent2El && parent2El.id !== "side-menu") parent2El.classList.add("mm-show");
    if (parent) {
      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;
      if (parent2) parent2.classList.add("mm-show");
    }
    ref.current.getScrollElement().scrollTop = item.offsetTop - 300;
  }, []);

  useEffect(() => {
    new MetisMenu("#side-menu");
  }, []);

  // Filtrage menu et sous-menu selon le rôle
  const filteredMenu = menuItems
    .filter(item => item.allowedRoles.map(r => r.toLowerCase()).includes(role))
    .map(item => {
      if (item.subMenu) {
        const filteredSub = item.subMenu.filter(sub =>
          sub.allowedRoles?.map(r => r.toLowerCase()).includes(role)
        );
        return { ...item, subMenu: filteredSub };
      }
      return item;
    })
    .filter(item => !item.subMenu || item.subMenu.length > 0); // retirer parent vide

  return (
    <SimpleBar className="h-100" ref={ref}>
      <div id="sidebar-menu">
        <ul className="metismenu list-unstyled" id="side-menu">
          {filteredMenu.map((item, idx) => (
            <li key={idx}>
              {item.subMenu && item.subMenu.length > 0 ? (
                <>
                  <Link to="/#" className="has-arrow">
                    <i className={item.icon}></i>
                    <span>{t(item.title)}</span>
                  </Link>
                  <ul className="sub-menu">
                    {item.subMenu.map((sub, i) => (
                      <li key={i}>
                        <Link to={sub.path}>{t(sub.title)}</Link>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <Link to={item.path}>
                  <i className={item.icon}></i>
                  <span>{t(item.title)}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </SimpleBar>
  );
};

SidebarContent.propTypes = {
  t: PropTypes.any,
  authUser: PropTypes.object,
};

const mapStateToProps = (state) => ({
  authUser: state.Login?.authUser, // adapter selon ton store Redux
});

export default connect(mapStateToProps)(withRouter(withTranslation()(SidebarContent)));